import React from 'react';

interface FramerMotionVectorWavesProps {
  className?: string;
  speedMultiplier?: number;
  opacity?: number;
}

/**
 * FramerMotionVectorWaves renders crisp, hardware-accelerated SVG vector waves.
 * Reflow-optimized: uses pure SVG GPU composite rendering to eliminate forced DOM reflows.
 */
export default function FramerMotionVectorWaves({
  className = 'absolute inset-0 w-full h-full pointer-events-none overflow-hidden',
  opacity = 0.55,
}: FramerMotionVectorWavesProps) {
  
  // Wave 1: Delta Wave (Deep, Slow, Purple-Indigo Glow)
  const deltaA = "M 0 160 C 300 80, 600 240, 900 120 C 1200 40, 1300 200, 1440 160 L 1440 320 L 0 320 Z";

  // Wave 2: Theta Wave (Middle, Calming, Emerald/Cyan Glow)
  const thetaA = "M 0 120 C 400 220, 700 40, 1000 180 C 1250 260, 1350 100, 1440 120 L 1440 320 L 0 320 Z";

  // Wave 3: Alpha Wave (Top, Fine, Lucid Emerald Light Glow Line only)
  const alphaA = "M 0 90 C 250 170, 500 30, 750 150 C 1000 230, 1250 70, 1440 90";

  // Pure Vector Stroke Wave for technical/scientific complexity representation
  const fineGridA = "M 0 60 Q 360 140, 720 60 T 1440 60";

  return (
    <div className={`${className} select-none`} style={{ opacity }} aria-hidden="true">
      <svg 
        viewBox="0 0 1440 320" 
        preserveAspectRatio="none" 
        className="w-full h-full"
      >
        <defs>
          {/* Deep Delta Glow Gradient */}
          <linearGradient id="deltaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(99, 102, 241, 0.12)" />
            <stop offset="50%" stopColor="rgba(16, 185, 129, 0.08)" />
            <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
          </linearGradient>

          {/* Calming Theta Gradient */}
          <linearGradient id="thetaGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(16, 185, 129, 0.16)" />
            <stop offset="60%" stopColor="rgba(6, 182, 212, 0.14)" />
            <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
          </linearGradient>

          {/* Glowing filter for elite UX */}
          <filter id="vectorGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 1. Delta Background Filled Wave */}
        <path
          d={deltaA}
          fill="url(#deltaGradient)"
          className="transition-opacity duration-1000"
        />

        {/* 2. Theta Midground Filled Wave */}
        <path
          d={thetaA}
          fill="url(#thetaGradient)"
          className="transition-opacity duration-1000"
        />

        {/* 3. High-Contrast Scientific Stroke Wave */}
        <path
          d={fineGridA}
          fill="none"
          stroke="rgba(255, 255, 255, 0.03)"
          strokeWidth="1"
          strokeDasharray="4,8"
        />

        {/* 4. Fine Foreground Alpha Stroke Line with Glow */}
        <path
          d={alphaA}
          fill="none"
          stroke="url(#thetaGradient)"
          strokeWidth="1.8"
          filter="url(#vectorGlow)"
          opacity="0.8"
        />
      </svg>
    </div>
  );
}
